import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import { Box, ButtonBase, IconButton, MenuItem, Select, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { CourseEntity } from 'src/entities/Course.entity';
import { useSortCoursesMutation } from 'src/hooks/useCourseMutations';
import { parseCourseMasterError } from 'src/utils/courseMasterError';

import { nextTripOptions, reorderIds, tripLabel } from './courseMasterUtils';

const COLS = '64px minmax(0, 1.2fr) 72px minmax(0, 1fr)';
const muted = 'text.secondary';

type Props = { courses: CourseEntity[]; selectedCourseId: number | null; onSelect: (courseId: number) => void };

/** コース一覧（便ごとに 1 行。D21 決定 11）。並び替えは全件の並び（sort_order）を PUT sort_orders で送る */
export default function CourseList({ courses, selectedCourseId, onSelect }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const sort = useSortCoursesMutation();
  const [tripNo, setTripNo] = useState<number | ''>('');
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const filtered = useMemo(
    () => courses.filter((c) => tripNo === '' || c.tripNo === tripNo),
    [courses, tripNo],
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const ids = reorderIds(courses.map((c) => c.id), Number(active.id), Number(over.id));
    sort.mutate({ ids }, { onError: (e) => enqueueSnackbar(parseCourseMasterError(e, '並び順を保存できませんでした').message, { variant: 'error' }) });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ display: 'flex', gap: 1, p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Select<number | ''>
          size="small"
          displayEmpty
          value={tripNo}
          onChange={(e) => setTripNo(e.target.value === '' ? '' : Number(e.target.value))}
          inputProps={{ 'aria-label': '便区分で絞り込む' }}
          sx={{ fontSize: 13, height: 32, minWidth: 120 }}
        >
          <MenuItem value="" sx={{ fontSize: 13 }}>便区分：すべて</MenuItem>
          {nextTripOptions(courses).slice(0, -1).map((n) => <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>{tripLabel(n)}</MenuItem>)}
        </Select>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: `28px ${COLS}`, gap: 1, px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider', fontSize: 12, color: muted, fontWeight: 600, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
          <span /><span>コース番号</span><span>コース名</span><span>便区分</span><span>所属ダイヤ</span>
        </Box>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={filtered.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            {filtered.map((c) => (
              <CourseListRow key={c.id} course={c} selected={c.id === selectedCourseId} disabled={sort.isPending} onClick={() => onSelect(c.id)} />
            ))}
          </SortableContext>
        </DndContext>
        {filtered.length === 0 && <Box sx={{ p: 2, fontSize: 13, color: muted }}>該当するコースがありません</Box>}
      </Box>
    </Box>
  );
}

function CourseListRow({ course, selected, disabled, onClick }: { course: CourseEntity; selected: boolean; disabled: boolean; onClick: () => void }) {
  const { setNodeRef, setActivatorNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id: course.id, disabled });
  const label = `${course.code}${course.name ? ` ${course.name}` : ''} ${tripLabel(course.tripNo)}`;
  return (
    <Box ref={setNodeRef} data-testid="course-row"
      sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, borderBottom: 1, borderColor: 'divider', position: 'relative',
        transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 2 : 'auto',
        bgcolor: selected ? 'action.selected' : 'background.paper' }}>
      <Tooltip title="ドラッグで並び替え">
        <IconButton
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          size="small"
          aria-label={`${label}をドラッグで並び替え`}
          sx={{ width: 24, p: 0, cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none', color: muted }}
        >
          <DragIndicatorRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
      <ButtonBase onClick={onClick} aria-label={`${label}を開く`}
        sx={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', textAlign: 'left', py: 1.25, gap: 1, color: 'text.primary' }}>
        <Box component="span" sx={{ fontSize: 15, fontWeight: 700 }}>{course.code}</Box>
        <Box component="span" sx={{ fontSize: 13, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: course.name ? 'text.primary' : muted }}>{course.name || '未設定'}</Box>
        <Box component="span" sx={{ fontSize: 13 }}>{course.firstTripCourse ? `${tripLabel(course.tripNo)}（${course.firstTripCourse.code}）` : tripLabel(course.tripNo)}</Box>
        <Box component="span" sx={{ fontSize: 13, color: muted, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {course.timetables.length ? course.timetables.map((t) => t.name).join(' / ') : '未所属'}
        </Box>
      </ButtonBase>
    </Box>
  );
}
